import React, { useEffect, useState } from 'react';
import Joi from 'joi';
import { joiResolver } from '@hookform/resolvers/joi';
import { useForm, FormProvider } from 'react-hook-form';
import axios from 'axios';
import styled, { css } from 'styled-components';
import { _ } from '_locales';
import { colors } from '_styles';
import { eventTracker } from '_helpers';
import { WaitScreen } from '_components';

import ReminderSuccessScreen from './ReminderSuccessScreen';
import { email, phone } from '../../../helpers/validators';
import {
  Collections, AlertReminder, ReminderCommMethod, ErrorText
} from '../components';
import apis from '../../../app/apis';
import HOC from '../../HOC';
import provideRecaptchaToken from '../helpers/recaptchaToken';
import Verification from '../components/Verification';
import requestPin from '../helpers/requestPin';
import ServerError from '../helpers/errors';
import SubscribeToNotifications from './SubscribeToNotifications';
import { convertPhoneNumberToDigits } from '../../BulkPickup/heplers';
import { cts_secure } from '../../../app/cts';

const Button = styled.button`
  font-size: 14px !important;
   
  padding: 20px 0;
  background-color: ${colors.RC_ACTION};
  cursor: pointer;
  border: none;
  border-radius: 8px;
  color: #fff;
  width: 160px;
  height: 39px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  text-transform: capitalize;

  ${(props) => props.disabled && css`
    cursor: not-allowed;
    background-color: ${colors.GREY}
  `}
`;

const Container = styled.div`
  margin-top: 20px;
  padding: 0 15px;
  box-sizing: border-box;
  font-size: 12px !important;
   
`;

const ButtonContainer = styled.div`
  text-align: center;
  margin: 20px 0 50px 0;
`;

const schema = Joi.object({
  collections: Joi.array()
    .items(Joi.string())
    .required()
    .min(1)
    .messages({
      'any.required': _('reminder.please_select_one_or_more_collections'),
      'array.min': _('reminder.please_select_one_or_more_collections'),
    }),
  alert: Joi.string()
    .required()
    .messages({
    }),
  method: Joi.string()
    .required()
    .valid('email', 'sms', 'phone')
    .default('email')
    .messages({
      'any.only': _('reminder.please_select_one_of_reminding_methods')
    }),
  email: Joi.alternatives()
    .conditional('method',
      {
        is: 'email',
        then: email(),
        otherwise: Joi.any()
      }),
  sms: Joi.alternatives()
    .conditional('method',
      {
        is: 'sms',
        then: phone(_('general.mobile')),
        otherwise: Joi.any()
      }),
  phone: Joi.alternatives()
    .conditional('method',
      {
        is: 'phone',
        then: phone(_('general.phone')),
        otherwise: Joi.any()
      }),
  areaCode: Joi.string(),
  subscribe_to_notifications: Joi.boolean(),
  // pin: Joi.number().required().messages({ 'any.required': _('reminder.pin_is_required') })
});

const SingleReminder = ({ plugin, config, zone }) => {
  const [formData, setFormData] = useState({
    collections: [],
    alert: 12,
    method: 'email',
    email: '',
    sms: '',
    phone: '',
    areaCode: '1',
    subscribe_to_notifications: true
  });

  const [formattedPhone, setFormattedPhone] = useState('');
  const [formattedSms, setFormattedSms] = useState('');

  const unformattedSms = convertPhoneNumberToDigits(formattedSms);
  const unformattedPhone = convertPhoneNumberToDigits(formattedPhone);

  const methods = useForm({
    resolver: joiResolver(schema),
    defaultValues: formData
  });
  const {
    trigger, getValues, reset,
  } = methods;

  const PAGE_STATE = { FORM: 'form', SUCCESS: 'success' };
  const FORM_STATE = { INPUT: 'input', VERIFY: 'verify' };

  const [page, setPage] = useState(PAGE_STATE.FORM);
  const [formState, setFormState] = useState(FORM_STATE.INPUT);
  const [errorMsg, setErrorMsg] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    reset(formData);
  }, [formState]);

  const { projectId: project_id } = plugin;
  const { districtId: district_id } = config;
  const { zoneStr: zone_id, fullAddress, mapCityName } = zone;

  const onNext = async (e) => {
    // After the user enter the email address/phone number and hit the "Create Reminder" button
    e.preventDefault();
    const isValid = await trigger();
    if (!isValid) {
      return;
    }
    if ((getValues().method === 'sms' && unformattedSms.length !== 10)
      || (getValues().method === 'phone' && unformattedPhone.length !== 10)
    ) {
      setErrorMsg('Phone number is not valid!');
      return;
    }

    setLoading(true);
    try {
      if (await requestPin({
        ...getValues(),
        sms: unformattedSms,
        phone: unformattedPhone
      })) {
        setFormData({
          ...formData,
          ...getValues(),
          sms: unformattedSms,
          phone: unformattedPhone
        });
        setFormState(FORM_STATE.VERIFY);
      } else {
        setErrorMsg('An error occurred. We couldn\'t verify your email/number');
      }
    } catch (error) {
      if (error.isAxiosError && error.response.status === 400) {
        setErrorMsg('Seems like you entered invalid data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (e) => {
    // When the user enters the 6-digit pin and hits "Create Reminder" button
    e.preventDefault();
    setErrorMsg('');
    setLoading(true);
    const {
      collections,
      method,
      email,
      areaCode,
      pin,
      alert: remindBefore,
      subscribe_to_notifications,
    } = { ...formData, ...getValues() };
    if (!pin) {
      setErrorMsg('Please enter the 6-digit code that is sent to you.');
      return;
    }
    setLoading(true);
    try {
      eventTracker(`Set Reminder - ${method}`, '');
      const token = await provideRecaptchaToken();
      const reminderData = {
        token,
        project_id,
        district_id,
        zone_id,
        pin,
        // auto select if there is only one collection and hide collection list
        collection_ids: Object.keys(config?.collection)?.length === 1
          ? [+Object.keys(config?.collection)[0].split('-')[1]]
          : collections.map((item) => Number(item)),
        remind_before: [Number(remindBefore)],
        ...(method === 'phone' && ({ country_cd: areaCode, phone_num: unformattedPhone })),
        ...(method === 'sms' && ({ country_cd: areaCode, sms_num: unformattedSms })),
        ...(method === 'email' && ({ email })),
        subscribe_to_notifications,
        address: fullAddress || mapCityName?.search_nm || ''
      };
      const { data } = await cts_secure('post', `${apis.reminderSignUp}/reminder/single/${method}`, reminderData);
      if (!data || !data.success) {
        throw new ServerError(data.message);
      }
      setPage(PAGE_STATE.SUCCESS);
    } catch (e) {
      if (e instanceof ServerError) {
        setErrorMsg(e.message);
      } else {
        setErrorMsg('An unexpected error occurred! Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const {
    reminderOption: {
      has_phone_reminder,
      has_sms_reminder,
    }
  } = config;

  return (
    <>
      {
        page === PAGE_STATE.FORM && (
          <Container>
            {loading && (<WaitScreen />)}
            <FormProvider {...methods}>
              <form action="submit">
                {
                  formState === FORM_STATE.INPUT && (
                    <div>
                      {Object.keys(config.collection)?.length > 1 && (
                        <Collections
                          allCollections={config.collection}
                        />
                      )}
                      <AlertReminder
                        collections={config?.collection}
                      />
                      <ReminderCommMethod
                        reminder="single"
                        has_phone_reminder={has_phone_reminder}
                        has_sms_reminder={has_sms_reminder}
                        setFormattedPhone={setFormattedPhone}
                        setFormattedSms={setFormattedSms}
                        formattedSms={formattedSms}
                        formattedPhone={formattedPhone}
                      />
                      <SubscribeToNotifications />
                      <ButtonContainer>
                        <Button
                          type="button"
                          onClick={onNext}
                          aria-label="confirm to set reminder"
                        >
                          {_('reminder.create_reminder')}
                        </Button>
                      </ButtonContainer>
                    </div>
                  )
                }
                {formState === FORM_STATE.VERIFY && (
                  <div>
                    <Verification
                      formData={formData}
                      goBack={() => { setFormState(FORM_STATE.INPUT); }}
                      resend={() => requestPin(formData)}
                      setLoading={setLoading}
                    />
                    <ButtonContainer>
                      <Button
                        type="submit"
                        onClick={onSubmit}
                        aria-label="confirm to set reminder"
                      >
                        {_('reminder.create_reminder')}
                      </Button>
                    </ButtonContainer>
                  </div>
                )}
              </form>
            </FormProvider>
          </Container>
        )
      }
      {
        errorMsg ? (
          <ButtonContainer>
            <ErrorText>{errorMsg}</ErrorText>
          </ButtonContainer>
        ) : null
      }
      {
        page === PAGE_STATE.SUCCESS && <ReminderSuccessScreen />
      }
    </>
  );
};

export default HOC(SingleReminder);
